import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination,
  Chip,
} from "@mui/material";
import api from "../../utils/api";
import { ThemeContext } from "../../utils/ThemeContext";
import UserActions from "./UserActions";
import UserFormDialog from "./UserFormDialog";
import UserDeleteDialog from "./UserDeleteDialog";
import BulkInviteButton from "./BulkInviteButton";
import UserResetPasswordDialog from "./UserResetPasswordDialog";
import UserResetSuccessDialog from "./UserResetSuccessDialog";
import UserPermissionOverridesDialog from "./UserPermissionOverridesDialog";

const PropertyAccountsTable = () => {
  const { isDark } = useContext(ThemeContext);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [permissionsUser, setPermissionsUser] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(400);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get("/PropertyAccounts");

      const userMap = new Map();

      response.data.forEach((row) => {
        if (!userMap.has(row.Username)) {
          userMap.set(row.Username, {
            Username: row.Username,
            UserID: row.UserID,
            Name: row.Name,
            Lastname: row.Lastname,
            Email: row.Email,
            Status: row.status || row.Status,
            AssociatedAccounts: Array.isArray(row.AssociatedAccounts)
              ? row.AssociatedAccounts
              : row.AssociatedAccounts
              ? String(row.AssociatedAccounts)
                  .split(",")
                  .map((a) => a.trim())
              : [],
            Roles: [],
          });
        }

        userMap.get(row.Username).Roles.push({
          RoleID: row.RoleID,
          RoleName: row.RoleName,
        });
      });

      setUsers(Array.from(userMap.values()));
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await api.get("/roles");
      setAvailableRoles(response.data);
    } catch (err) {
      console.error("Failed to fetch roles", err);
    }
  };

  const filteredUsers = users
  .filter((user) => user.Status?.toLowerCase() === "active")
  .filter((user) => {
    const normalizedSearch = searchTerm.toLowerCase();

    const fields = [
      user.Username,
      user.Name,
      user.Lastname,
      user.Email,
      ...(user.AssociatedAccounts || []),
    ];

    return fields.some(
      (value) => String(value).toLowerCase().includes(normalizedSearch)
    );
  });

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const pageUsernames = paginatedUsers.map((user) => user.Username);
      setSelectedUsers((prev) => [...new Set([...prev, ...pageUsernames])]);
    } else {
      const pageUsernames = paginatedUsers.map((user) => user.Username);
      setSelectedUsers((prev) =>
        prev.filter((username) => !pageUsernames.includes(username))
      );
    }
  };

  const isAllSelected =
    paginatedUsers.length > 0 &&
    paginatedUsers.every((user) => selectedUsers.includes(user.Username));

  const isIndeterminate =
    paginatedUsers.some((user) => selectedUsers.includes(user.Username)) &&
    !isAllSelected;

  const handleEditUser = (user) => {
    setFormData({
      ...user,
      Roles: user.Roles?.map((r) => r.RoleID) || [],
    });
    setFormMode("edit");
    setShowForm(true);
  };

  const handleSubmitUser = async () => {
    try {
      if (formMode === "edit") {
        await api.put(`/users/${formData.UserID}`, formData);
      } else {
        if (!formData.Password || !formData.Username) {
          alert("Username and password are required.");
          return;
        }

        await api.post("/addUser", {
          username: formData.Username,
          password: formData.Password,
          email: formData.Email || "default@example.com",
          name: formData.Name || "",
          lastname: formData.Lastname || "",
          roles: [3],
          sendInvitationEmail: formData.SendInvite || false,
          associatedAccounts: formData.AssociatedAccounts || [],
        });
      }

      setShowForm(false);
      loadUsers();
    } catch (error) {
      console.error("Error submitting user:", error);

      const message =
        error.response?.data?.message ||
        error.response?.statusText ||
        error.message ||
        "Unknown error occurred.";

      alert(`Failed to submit user:\n${message}`);
    }
  };

  const handleResetPassword = (username) => {
    setUserToReset(username);
    setShowResetDialog(true);
  };

  const confirmResetPassword = async () => {
    try {
      const res = await api.post(`/users/${userToReset}/resetPassword`);
      setResetResult({
        username: userToReset,
        password: res.data.message?.split(": ")[1],
      });
    } catch (err) {
      console.error("Failed to reset password:", err);
      alert("Failed to reset password.");
    } finally {
      setShowResetDialog(false);
    }
  };

  const handleDeleteUser = (username) => {
    setUserToDelete(username);
    setShowDeleteDialog(true);
  };

  const handleSelectUser = (username) => {
    setSelectedUsers((prev) =>
      prev.includes(username)
        ? prev.filter((u) => u !== username)
        : [...prev, username]
    );
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const getStatusChip = (status) => {
    const isActive = status?.toLowerCase() === "active";

    return (
      <Chip
        label={status || "Unknown"}
        size="small"
        sx={{
          borderRadius: "8px",
          fontWeight: 700,
          backgroundColor: isActive
            ? "rgba(46,125,50,0.10)"
            : "rgba(211,47,47,0.10)",
          color: isActive ? "#2e7d32" : "#d32f2f",
        }}
      />
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: isDark ? "#dde5f0" : "#223142",
              mb: 0.5,
            }}
          >
            Member Accounts
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: isDark ? "#8a9ab8" : "#6b778c",
            }}
          >
            Manage homeowner accounts, associated properties, and invitations.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", md: "auto" },
          }}
        >
          <TextField
            label="Search Users"
            size="small"
            fullWidth
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              ...fieldSx,
              minWidth: { xs: "100%", md: 280 },
              maxWidth: { xs: "100%", md: 380 },
            }}
          />

          <Button
            variant="contained"
            sx={primaryButtonSx}
            onClick={() => {
              setFormData({
                Username: "",
                Password: "",
                Name: "",
                Lastname: "",
                Email: "",
                SendInvite: true,
                AssociatedAccounts: [],
              });
              setFormMode("add");
              setShowForm(true);
            }}
          >
            New User
          </Button>

          <BulkInviteButton
            selectedUsers={selectedUsers}
            onSuccess={() => console.log("Invites sent!")}
          />
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          display: { xs: "none", lg: "block" },
          width: "100%",
          overflowX: "auto",
          overflowY: "visible",
          borderRadius: "16px",
          border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          backgroundColor: isDark ? "#16223a" : "rgba(255,255,255,0.96)",
        }}
      >
        <Table
          size="small"
          sx={{ minWidth: 860, width: "100%" }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{
                  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#eef2fb",
                  width: 50,
                  borderBottom: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  sx={{ p: 0.5 }}
                />
              </TableCell>

              {[
                "Username",
                "Name",
                "Lastname",
                "Email",
                "Associated Accounts",
                "Status",
                "Actions",
              ].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#eef2fb",
                    color: isDark ? "#dde5f0" : "#223142",
                    whiteSpace: "nowrap",
                    borderBottom: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map((user) => (
                <TableRow key={user.Username} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.includes(user.Username)}
                      onChange={() => handleSelectUser(user.Username)}
                      color="primary"
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>

                  <TableCell>{user.Username}</TableCell>
                  <TableCell>{user.Name}</TableCell>
                  <TableCell>{user.Lastname}</TableCell>

                  <TableCell
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: 240,
                    }}
                  >
                    {user.Email}
                  </TableCell>

                  <TableCell
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: 260,
                    }}
                  >
                    {user.AssociatedAccounts?.join(", ") || "None"}
                  </TableCell>

                  <TableCell>{getStatusChip(user.Status)}</TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <UserActions
                        user={user}
                        onEdit={handleEditUser}
                        onResetPassword={handleResetPassword}
                        onDelete={handleDeleteUser}
                        onManagePermissions={setPermissionsUser}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} sx={{ color: "text.secondary" }}>
                  No member accounts found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: { xs: "block", lg: "none" } }}>
        {paginatedUsers.length > 0 ? (
          paginatedUsers.map((user) => (
            <Paper
              key={user.Username}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: "16px",
                border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                backgroundColor: isDark ? "#1e2e47" : "rgba(255,255,255,0.96)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  mb: 1.5,
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={selectedUsers.includes(user.Username)}
                    onChange={() => handleSelectUser(user.Username)}
                    color="primary"
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {user.Username}
                    </Typography>
                    <Typography variant="body2" sx={{ color: isDark ? "#8a9ab8" : "#6b778c" }}>
                      {user.Name} {user.Lastname}
                    </Typography>
                  </Box>
                </Box>

                {getStatusChip(user.Status)}
              </Box>

              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Email:</strong> {user.Email}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Accounts:</strong>{" "}
                {user.AssociatedAccounts?.join(", ") || "None"}
              </Typography>

              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <UserActions
                  user={user}
                  onEdit={handleEditUser}
                  onResetPassword={handleResetPassword}
                  onDelete={handleDeleteUser}
                />
              </Box>
            </Paper>
          ))
        ) : (
          <Paper
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
              backgroundColor: isDark ? "#1e2e47" : "rgba(255,255,255,0.96)",
            }}
          >
            <Typography sx={{ color: "text.secondary" }}>
              No member accounts found.
            </Typography>
          </Paper>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: "14px",
          border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
          overflow: "hidden",
          backgroundColor: isDark ? "#16223a" : "rgba(255,255,255,0.96)",
        }}
      >
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[50, 100, 200, 400, 1000]}
        />
      </Paper>

      <UserFormDialog
        open={showForm}
        mode={formMode}
        formData={formData}
        setFormData={setFormData}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmitUser}
        reloadUsers={loadUsers}
        availableRoles={availableRoles}
        showAssociatedAccount={true}
        hideRoleSelection={true}
      />

      <UserResetPasswordDialog
        open={showResetDialog}
        username={userToReset}
        onClose={() => setShowResetDialog(false)}
        onConfirm={confirmResetPassword}
      />

      <UserResetSuccessDialog
        open={!!resetResult}
        username={resetResult?.username}
        password={resetResult?.password}
        onClose={() => setResetResult(null)}
      />

      <UserDeleteDialog
        open={showDeleteDialog}
        username={userToDelete}
        onClose={() => setShowDeleteDialog(false)}
        onDelete={async () => {
          try {
            await api.put(`/users/${userToDelete}/deactivate`);
            setShowDeleteDialog(false);
            loadUsers();
          } catch (err) {
            console.error("Failed to deactivate user:", err);
            alert("Failed to deactivate user.");
          }
        }}
      />

      <UserPermissionOverridesDialog
        open={!!permissionsUser}
        user={permissionsUser}
        onClose={() => setPermissionsUser(null)}
      />
    </Box>
  );
};

export default PropertyAccountsTable;
